import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Shield, Loader, CheckCircle, AlertCircle, Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match"); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok) { setDone(true); setTimeout(() => navigate("/login"), 2500); }
      else setError(data.detail || "Reset failed. The link may have expired.");
    } catch {
      setError("Unable to connect. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading font-bold text-slate-800 text-2xl">Set new password</h1>
          <p className="text-slate-500 text-sm mt-1">Must be at least 8 characters</p>
        </div>

        {done ? (
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center shadow-sm">
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
            <p className="font-semibold text-slate-800 mb-1">Password updated!</p>
            <p className="text-slate-500 text-sm">Redirecting to login...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">New password</label>
              <div className="relative">
                <input required type={show ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="At least 8 characters" data-testid="reset-password"
                  className="w-full h-10 px-3 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
                <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Confirm password</label>
              <input required type={show ? "text" : "password"} value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="Repeat password" data-testid="reset-confirm"
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
            </div>
            {error && <div className="flex items-center gap-2 text-red-600 text-sm"><AlertCircle className="w-4 h-4" />{error}</div>}
            <button type="submit" disabled={loading} data-testid="reset-submit"
              className="w-full h-10 flex items-center justify-center gap-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition-colors disabled:opacity-60">
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Updating..." : "Update Password"}
            </button>
            <Link to="/login" className="block text-center text-sm text-slate-500 hover:text-navy transition-colors">Back to login</Link>
          </form>
        )}
      </div>
    </div>
  );
}
